import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { Loader2, Mail, SquareUserRoundIcon } from 'lucide-react';
import { Link } from 'react-router-dom';
import ReCAPTCHA from 'react-google-recaptcha';
import { useState } from 'react';
import { run } from 'mailcheck';

import {
  Input,
  Button,
  Form,
  FormField,
  FormItem,
  FormControl,
  FormLabel,
  FormMessage,
  FormDescription,
  cn,
  PasswordInput,
  Checkbox,
} from '@sandtech-hub-monorepo/ui-kit';

import {
  emailLabel,
  emailPlaceholder,
  passwordLabel,
  firstNameLabel,
  firstNamePlaceholder,
  lastNameLabel,
  lastNamePlaceholder,
  signUpButtonLabel,
  passwordDescription,
  firstNameRequiredValidationMessage,
  lastNameRequiredValidationMessage,
  passwordRulesValidationMessage,
  maxLengthExceededValidationMessage,
  passwordPlaceholder,
  nameValidationMessage,
  emailRequiredValidationMessage,
  confirmPasswordLabel,
  confirmPasswordPlaceholder,
  confirmPasswordSignUpRequiredValidationMessage,
  signUpPasswordMismatchMessage,
} from '../../../../../stringsManager';

import {
  nameRegex,
  passwordValidationRegex,
} from 'libs/ui-auth-kit/src/lib/constants';

const createFormSchema = (
  recaptchaSiteKey?: string,
  hideNameFields?: boolean,
  showConfirmPassword?: boolean
) =>
  z
    .object({
    // When hidden, there's no input to validate - Cognito's given_name/
    // family_name are still `required` at the User Pool level, so an empty
    // string is sent (a later, blocking onboarding step is expected to
    // overwrite it via updateUserAttributes, which both attributes allow).
    firstName: hideNameFields
      ? z
          .string()
          .optional()
          .transform((value) => value ?? '')
      : z
          .string()
          .min(1, firstNameRequiredValidationMessage)
          .max(32, maxLengthExceededValidationMessage)
          .regex(new RegExp(nameRegex), nameValidationMessage),
    lastName: hideNameFields
      ? z
          .string()
          .optional()
          .transform((value) => value ?? '')
      : z
          .string()
          .min(1, lastNameRequiredValidationMessage)
          .regex(new RegExp(nameRegex), nameValidationMessage),
    email: z.string().min(1, emailRequiredValidationMessage).email(),
    password: z
      .string()
      .regex(new RegExp(passwordValidationRegex), passwordRulesValidationMessage),
    // Always present so the object's inferred shape (and .refine below)
    // don't vary by branch - when hidden it's never rendered or touched by
    // the user, so it stays '' and the refine check is skipped entirely.
    confirmPassword: showConfirmPassword
      ? z.string().min(1, confirmPasswordSignUpRequiredValidationMessage)
      : z
          .string()
          .optional()
          .transform((value) => value ?? ''),
    marketingConsent: z.boolean().optional(),
    recaptchaToken: recaptchaSiteKey
      ? z.string().min(1, 'Please complete the CAPTCHA verification')
      : z.string().optional(),
    })
    .refine(
      (values) =>
        !showConfirmPassword || values.password === values.confirmPassword,
      {
        message: signUpPasswordMismatchMessage,
        path: ['confirmPassword'],
      }
    );

export interface SignUpFormProps {
  onSubmit: (values: z.infer<ReturnType<typeof createFormSchema>>) => void;
  loading: boolean;
  className?: string;
  privacyPolicyLink: string;
  termsOfServiceLink: string;
  recaptchaSiteKey?: string;
  brandName: string;
  /**
   * Hides the First Name/Last Name fields. Only intended for platforms with
   * a later, blocking onboarding step that collects the name instead (see
   * the schema comment above for what this means for Cognito's required
   * given_name/family_name attributes). Defaults to false, so every
   * existing caller keeps both fields.
   */
  hideNameFields?: boolean;
  /**
   * Adds a "Confirm password" field, validated to match `password`.
   * Defaults to false, so every existing caller keeps today's single
   * password field.
   */
  showConfirmPassword?: boolean;
  /**
   * Hides the marketing-communications consent checkbox. `marketingConsent`
   * stays in the schema/defaultValues either way (it's optional and
   * unvalidated, and `handleSignUp` in SignUp/index.tsx only writes the
   * custom:agreedToComsAt attribute when it's true) - hiding the checkbox
   * just means it never becomes true. Defaults to false, so every existing
   * caller keeps the checkbox.
   */
  hideMarketingConsent?: boolean;
  /**
   * Restyles both the terms-of-service and privacy-policy links. Defaults
   * to today's plain `underline` (inheriting the surrounding text color),
   * so every existing caller is unaffected.
   */
  legalLinkClassName?: string;
  /**
   * Replaces "{brandName}'s" before "privacy policy" (e.g. with "the").
   * Defaults to `${brandName}'s`, matching today's text for every existing
   * caller.
   */
  privacyPolicyPrefix?: string;
  submitButtonLabel?: string;
}

export const SignUpForm = ({
  className,
  loading,
  onSubmit,
  privacyPolicyLink,
  termsOfServiceLink,
  recaptchaSiteKey,
  brandName,
  hideNameFields,
  showConfirmPassword,
  hideMarketingConsent,
  legalLinkClassName,
  privacyPolicyPrefix,
  submitButtonLabel = signUpButtonLabel,
}: SignUpFormProps) => {
  const formSchema = createFormSchema(
    recaptchaSiteKey,
    hideNameFields,
    showConfirmPassword
  );
  const [emailSuggestion, setEmailSuggestion] = useState<string | null>(null);

  const form = useForm<z.infer<typeof formSchema>>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      firstName: '',
      lastName: '',
      email: '',
      password: '',
      confirmPassword: '',
      marketingConsent: false,
      recaptchaToken: undefined,
    },
  });

  const handleEmailBlur = (email: string) => {
    if (email) {
      run({
        email,
        suggested: (suggestion: { full: string }) => {
          setEmailSuggestion(suggestion.full);
        },
        empty: () => {
          setEmailSuggestion(null);
        },
      });
    }
  };

  return (
    <div className={cn('grid', className)}>
      <Form {...form}>
        <form
          onSubmit={form.handleSubmit(onSubmit)}
          className="flex flex-col gap-3"
        >
          {!hideNameFields && (
            <FormField
              control={form.control}
              name="firstName"
              render={({ field }) => (
                <FormItem>
                  <FormLabel className="font-normal">
                    {firstNameLabel}
                  </FormLabel>
                  <FormControl>
                    <Input
                      placeholder={firstNamePlaceholder}
                      endAdornment={<SquareUserRoundIcon />}
                      {...field}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
          )}

          {!hideNameFields && (
            <FormField
              control={form.control}
              name="lastName"
              render={({ field }) => (
                <FormItem>
                  <FormLabel className="font-normal">
                    {lastNameLabel}
                  </FormLabel>
                  <FormControl>
                    <Input
                      placeholder={lastNamePlaceholder}
                      endAdornment={<SquareUserRoundIcon />}
                      {...field}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
          )}

          <FormField
            control={form.control}
            name="email"
            render={({ field }) => (
              <FormItem>
                <FormLabel className="font-normal">{emailLabel}</FormLabel>
                <FormControl>
                  <Input
                    placeholder={emailPlaceholder}
                    {...field}
                    endAdornment={<Mail />}
                    onBlur={(e) => {
                      field.onBlur();
                      handleEmailBlur(e.target.value);
                    }}
                    onChange={(e) => {
                      field.onChange(e);
                      setEmailSuggestion(null);
                    }}
                  />
                </FormControl>
                {emailSuggestion && (
                  <p className="text-sm text-muted-foreground">
                    Did you mean{' '}
                    <button
                      type="button"
                      className="text-primary underline font-medium"
                      onClick={() => {
                        form.setValue('email', emailSuggestion);
                        setEmailSuggestion(null);
                      }}
                    >
                      {emailSuggestion}
                    </button>
                    ?
                  </p>
                )}
                <FormMessage />
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name="password"
            render={({ field }) => (
              <FormItem>
                <FormLabel className="font-normal">{passwordLabel}</FormLabel>
                <FormControl>
                  <PasswordInput {...field} placeholder={passwordPlaceholder} />
                </FormControl>
                <FormMessage />
                <FormDescription className="text-xs">
                  {passwordDescription}
                </FormDescription>
              </FormItem>
            )}
          />

          {showConfirmPassword && (
            <FormField
              control={form.control}
              name="confirmPassword"
              render={({ field }) => (
                <FormItem>
                  <FormLabel className="font-normal">
                    {confirmPasswordLabel}
                  </FormLabel>
                  <FormControl>
                    <PasswordInput
                      {...field}
                      placeholder={confirmPasswordPlaceholder}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
          )}

          <div className="flex flex-col gap-2">
            <div className="text-xs text-foreground leading-normal">
              By signing up I accept {brandName}'s{' '}
              <Link
                to={termsOfServiceLink}
                className={cn('underline', legalLinkClassName)}
                target="_blank"
                rel="noreferrer"
              >
                terms of services
              </Link>{' '}
              and acknowledge {privacyPolicyPrefix ?? `${brandName}'s`}{' '}
              <Link
                to={privacyPolicyLink}
                className={cn('underline', legalLinkClassName)}
                target="_blank"
                rel="noreferrer"
              >
                privacy policy
              </Link>
              .
            </div>

            {!hideMarketingConsent && (
              <FormField
                name="marketingConsent"
                control={form.control}
                render={() => (
                  <FormItem className="flex w-full gap-2 space-y-0">
                    <FormControl>
                      <Checkbox
                        className="mt-0.5"
                        id="marketingConsentFlag"
                        onCheckedChange={(checked) => {
                          form.setValue('marketingConsent', Boolean(checked));
                        }}
                      />
                    </FormControl>

                    <div className="flex flex-col gap-1">
                      <FormLabel
                        htmlFor="marketingConsentFlag"
                        className="text-foreground text-xs font-normal leading-normal"
                      >
                        I agree to receive commercial communications from{' '}
                        {brandName} through all available channels and may
                        opt out at any time.
                      </FormLabel>
                      <FormMessage />
                    </div>
                  </FormItem>
                )}
              />
            )}
          </div>

          {recaptchaSiteKey && (
            <FormField
              control={form.control}
              name="recaptchaToken"
              render={() => (
                <FormItem>
                  <FormControl>
                    <ReCAPTCHA
                      sitekey={recaptchaSiteKey}
                      onChange={(token) => {
                        form.setValue('recaptchaToken', token || '', {
                          shouldValidate: true,
                        });
                      }}
                      onExpired={() => {
                        form.setValue('recaptchaToken', '', {
                          shouldValidate: true,
                        });
                      }}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
          )}

          <Button disabled={loading} type="submit" className="w-full">
            {loading ? <Loader2 className="animate-spin" /> : submitButtonLabel}
          </Button>
        </form>
      </Form>
    </div>
  );
};
